import { motion as Motion } from "framer-motion";
import { useMemo, useState } from "react";
import cls from "./AnswerStageCard.module.scss";
import { resolveQuestionBackgroundUrl } from "./questionMedia";

const OPTIONS = ["A", "B", "C"];

export default function AnswerStageCard({ turn, players, onSubmit, loading }) {
  const [selection, setSelection] = useState("");

  const question = turn?.questionSnapshot || {};
  const backgroundUrl = resolveQuestionBackgroundUrl(question);

  const canSubmit = useMemo(
    () => Boolean(selection) && !loading,
    [selection, loading],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    await onSubmit(selection);
    setSelection("");
  };

  return (
    <section className={cls.card}>
      <div className={cls.glowPink} />
      <div className={cls.glowBlue} />
      <div className={cls.gridLines} />

      <div className={cls.header}>
        <p className={cls.kicker}>ANSWER PHASE</p>
        <h3 className={cls.title}>Lock in your answer</h3>
        <p className={cls.subtitle}>
          You are playing with{" "}
          <strong>{players?.guessPlayer?.displayName || "Guess Player"}</strong>
          . Select the option that you feel most and submit.
        </p>
      </div>

      <div className={cls.playersLine}>
        <span>
          <strong>{players?.guessPlayer?.displayName || "-"}</strong> is waiting
          for your answer.
        </span>
      </div>

      <div className={cls.questionCard}>
        {backgroundUrl ? (
          <div className={cls.backgroundWrap}>
            <div className={cls.backgroundMedia}>
              <Motion.img
                src={backgroundUrl}
                alt="Question background"
                className={cls.backgroundImage}
                initial={{ scale: 1, y: 0 }}
                animate={{ scale: 1.05, y: -10 }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut",
                }}
              />
            </div>

            <div className={cls.backgroundTopFade} />
            <div className={cls.backgroundBottomFade} />
            <div className={cls.backgroundGlow} />
          </div>
        ) : (
          <div className={cls.backgroundFallback} />
        )}

        <div className={cls.hudFrame}>
          <span className={cls.hudCornerTopLeft} />
          <span className={cls.hudCornerTopRight} />
          <span className={cls.hudCornerBottomLeft} />
          <span className={cls.hudCornerBottomRight} />
        </div>

        <div className={cls.questionOverlay}>
          <span className={cls.questionLabel}>QUESTION</span>

          <h4 className={cls.questionTitle}>{question.title || "Question"}</h4>

          {question.description ? (
            <p className={cls.questionDescription}>{question.description}</p>
          ) : null}

          {(question.images || []).length ? (
            <div className={cls.imageGrid}>
              {(question.images || []).map((image, index) =>
                image?.url ? (
                  <img
                    key={`${image.url}-${index}`}
                    src={image.url}
                    alt={`Question visual ${index + 1}`}
                    className={cls.questionImage}
                  />
                ) : null,
              )}
            </div>
          ) : null}
        </div>
      </div>

      <form className={cls.form} onSubmit={handleSubmit}>
        <div className={cls.optionsGrid}>
          {OPTIONS.map((option) => {
            const checked = selection === option;

            return (
              <label
                key={option}
                className={`${cls.optionCard} ${checked ? cls.optionActive : ""}`}
              >
                <input
                  type="radio"
                  name="answer_selection"
                  value={option}
                  checked={checked}
                  disabled={loading}
                  onChange={(event) => setSelection(event.target.value)}
                  className={cls.radioInput}
                />

                <div className={cls.optionTop}>
                  <span className={cls.optionBadge}>{option}</span>
                </div>

                <p className={cls.optionText}>
                  {question?.answers?.[option] || "No option text"}
                </p>
              </label>
            );
          })}
        </div>

        <button
          type="submit"
          className={cls.submitButton}
          disabled={!canSubmit}
        >
          {loading ? "Submitting..." : "Submit Answer"}
        </button>
      </form>
    </section>
  );
}
